import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  Dialog,
  email,
  Form,
  FormButton,
  ImageUploader,
  Input,
  normalizeUrl,
  number,
  phone,
  SelectInput,
  string,
  SwitchInput,
  url,
  useNotifications
} from '@ijoin/components';
import type { LeafHouseModelKeyEnum } from '@ijoin/hooks';
import {
  AdminLinkedMapRes,
  CreateManagedAccountProviderBody,
  UpdateManagedAccountProviderBody,
  UploadAssetBodyContentType,
  useGetAdminMapMethodologies,
  useGetAdminWhiteLabels,
  usePatchManagedAccountProviderById,
  usePostAdminManagedAccountProvider,
  useUploadAsset
} from '@ijoin/hooks';
import { Close, InfoOutlined, LockOutlined } from '@mui/icons-material';
import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Theme,
  Typography,
  useMediaQuery
} from '@mui/material';

import { orderBy } from 'lodash';
import { FC, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  MANAGED_ACCOUNT_PROVIDERS,
  MANAGED_ACCOUNT_PROVIDERS_WITHOUT_FUND_CONFIG
} from '../consts';
import { assetUrl, errorMessage } from '../helpers';

type FormProps = {
  description: string;
  disclosureStorage: string;
  dynamicQDIAAgeDefault: number;
  feeDisclosureMessage: string;
  managedAccountMethodologyId: string;
  modelKey: string;
  permitsDynamicQDIA: boolean;
  providerAdditionalDisclosure: string;
  providerAdvLink: string;
  providerDisplayEmail: string;
  providerDisplayName: string;
  providerDisplayPhoneNumber: string;
  providerLogoUri: string;
  setupComplete: boolean;
  whiteLabelId: string;
};

/** the models the AMA API accepts; the provider's choice drives every calculation it runs */
const MODEL_KEY_OPTIONS: { label: string; value: LeafHouseModelKeyEnum }[] = [
  { label: 'BLKRK1 — BlackRock', value: 'BLKRK1' },
  { label: 'CPGTD1 — Capital Group target date', value: 'CPGTD1' },
  { label: 'FKGOE1 — Franklin Templeton GOE', value: 'FKGOE1' },
  { label: 'IJN001 — iJoin', value: 'IJN001' }
];

const resolver = zodResolver(
  z
    .object({
      description: string({ maxLength: 255, required: true }),
      disclosureStorage: string({ maxLength: 65535 }),
      dynamicQDIAAgeDefault: number({ min: 0 }),
      feeDisclosureMessage: string({ maxLength: 4096 }),
      managedAccountMethodologyId: string({ required: true }),
      modelKey: string({ maxLength: 255 }),
      permitsDynamicQDIA: z.boolean(),
      providerAdditionalDisclosure: string({ maxLength: 4096 }),
      providerAdvLink: url(),
      providerDisplayEmail: email({ required: false }),
      providerDisplayName: string({ maxLength: 255 }),
      providerDisplayPhoneNumber: phone(),
      providerLogoUri: string({ maxLength: 255 }),
      setupComplete: z.boolean(),
      whiteLabelId: string()
    })
    /** the AMA API resolves the model from the provider, so one has to be chosen for that methodology */
    .superRefine((data, ctx) => {
      if (
        data.managedAccountMethodologyId ===
          MANAGED_ACCOUNT_PROVIDERS.IJOIN_AMA &&
        !data.modelKey
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Model Key is required',
          path: ['modelKey']
        });
      }
    })
);

type ManagedAccountProviderFormDialogProps = {
  isOpen: boolean;
  managedAccountMethodologyId: string;
  onClose: () => void;
  onSuccess: () => void;
  provider?: AdminLinkedMapRes;
};

export const ManagedAccountProviderFormDialog: FC<
  ManagedAccountProviderFormDialogProps
> = props => {
  const notifications = useNotifications();
  const fullScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  const isCreating = !props.provider;

  const methodologies = useGetAdminMapMethodologies({
    query: {
      enabled: props.isOpen,
      select: data =>
        orderBy(data ?? [], 'managedAccountMethodologyName', 'asc').map(m => ({
          label: m.managedAccountMethodologyName,
          value: m.managedAccountMethodologyId
        }))
    }
  });

  const whitelabels = useGetAdminWhiteLabels({
    query: {
      enabled: props.isOpen,
      select: data => orderBy(data ?? [], 'name', 'asc')
    }
  });

  const whitelabelOptions = useMemo(
    () =>
      (whitelabels.data ?? []).map(wl => ({
        label: wl.advisoryOrganizationName
          ? `${wl.name ?? wl.whiteLabelId} (${wl.advisoryOrganizationName})`
          : (wl.name ?? wl.whiteLabelId),
        value: wl.whiteLabelId
      })),
    [whitelabels.data]
  );

  const defaultValues = useMemo<FormProps>(
    () => ({
      description: props.provider?.description ?? '',
      disclosureStorage: props.provider?.disclosureStorage ?? '',
      dynamicQDIAAgeDefault: props.provider?.dynamicQDIAAgeDefault ?? 45,
      feeDisclosureMessage: props.provider?.feeDisclosureMessage ?? '',
      managedAccountMethodologyId:
        props.provider?.managedAccountMethodologyId ??
        props.managedAccountMethodologyId,
      modelKey: props.provider?.modelKey ?? '',
      permitsDynamicQDIA: props.provider?.permitsDynamicQDIA ?? false,
      providerAdditionalDisclosure:
        props.provider?.providerAdditionalDisclosure ?? '',
      providerAdvLink: normalizeUrl(props.provider?.providerAdvLink),
      providerDisplayEmail: props.provider?.providerDisplayEmail ?? '',
      providerDisplayName: props.provider?.providerDisplayName ?? '',
      providerDisplayPhoneNumber:
        props.provider?.providerDisplayPhoneNumber ?? '',
      providerLogoUri: props.provider?.providerLogoUri ?? '',
      setupComplete: props.provider?.setupComplete ?? false,
      whiteLabelId: props.provider?.whiteLabelId ?? ''
    }),
    [props.provider, props.managedAccountMethodologyId]
  );

  const methods = useForm<FormProps>({
    defaultValues,
    mode: 'all',
    resetOptions: { keepDirtyValues: true },
    resolver,
    values: defaultValues
  });

  const providerLogoUri = methods.watch('providerLogoUri');
  const permitsDynamicQDIA = methods.watch('permitsDynamicQDIA');
  const selectedMethodologyId = methods.watch('managedAccountMethodologyId');

  const isIJoinAMA =
    selectedMethodologyId === MANAGED_ACCOUNT_PROVIDERS.IJOIN_AMA;
  const isNoConfigMethodology =
    !!selectedMethodologyId &&
    MANAGED_ACCOUNT_PROVIDERS_WITHOUT_FUND_CONFIG.has(selectedMethodologyId);
  const isMethodologyLocked = !isCreating && !!props.provider?.setupComplete;

  const createProvider = usePostAdminManagedAccountProvider({
    mutation: {
      onError: err =>
        notifications.error(
          errorMessage(err, 'Failed to create managed account provider!')
        )
    }
  });
  const updateProvider = usePatchManagedAccountProviderById({
    mutation: {
      onError: err =>
        notifications.error(
          errorMessage(err, 'Failed to update managed account provider!')
        )
    }
  });
  const uploadAsset = useUploadAsset({
    mutation: {
      onError: () => notifications.error('Failed to upload logo!')
    }
  });

  const onCancel = () => {
    methods.reset(defaultValues);
    props.onClose();
  };

  const onSubmit = async (data: FormProps) => {
    const body: CreateManagedAccountProviderBody &
      UpdateManagedAccountProviderBody = {
      description: data.description,
      disclosureStorage: data.disclosureStorage,
      dynamicQDIAAgeDefault: data.dynamicQDIAAgeDefault,
      feeDisclosureMessage: data.feeDisclosureMessage,
      managedAccountMethodologyId: data.managedAccountMethodologyId,
      modelKey: data.modelKey,
      permitsDynamicQDIA: data.permitsDynamicQDIA,
      providerAdditionalDisclosure: data.providerAdditionalDisclosure,
      providerAdvLink: data.providerAdvLink,
      providerDisplayEmail: data.providerDisplayEmail,
      providerDisplayName: data.providerDisplayName,
      providerDisplayPhoneNumber: data.providerDisplayPhoneNumber,
      providerLogoUri: data.providerLogoUri,
      setupComplete: data.setupComplete,
      /* the column is a nullable FK, so an unset white label must be omitted, not sent as '' */
      whiteLabelId: data.whiteLabelId || undefined
    };

    try {
      if (props.provider) {
        await updateProvider.mutateAsync({
          data: body,
          managedAccountProviderId: props.provider.managedAccountProviderId
        });
      } else {
        await createProvider.mutateAsync({ data: body });
      }
    } catch {
      /* the mutation's onError has already surfaced the reason */
      return;
    }

    notifications.success(
      props.provider
        ? 'Changes saved successfully!'
        : 'Managed account provider created.'
    );
    methods.reset(data);
    props.onSuccess();
    props.onClose();
  };

  const isUploading = uploadAsset.isPending;
  const isSaving = createProvider.isPending || updateProvider.isPending;
  const isSaveDisabled =
    (!isCreating && !methods.formState.isDirty) || isUploading || isSaving;
  const isLoading =
    methodologies.isLoading || (props.isOpen && whitelabels.isLoading);

  return (
    <Dialog
      data-testid='managedAccountProviderFormDialog'
      fullScreen={fullScreen}
      fullWidth
      isOpen={props.isOpen}
      maxWidth='md'
      onClose={onCancel}>
      <DialogTitle
        component='div'
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          pl: 3,
          pr: 1,
          py: 2
        }}>
        <Typography variant='h3'>
          {isCreating
            ? 'Create Managed Account Provider'
            : 'Edit Managed Account Provider'}
        </Typography>
        <IconButton
          aria-label='Close'
          data-testid='closeManagedAccountProviderDialog'
          onClick={onCancel}>
          <Close />
        </IconButton>
      </DialogTitle>
      {isLoading ? (
        <DialogContent dividers>
          <Stack alignItems='center' justifyContent='center' sx={{ p: 6 }}>
            <CircularProgress />
          </Stack>
        </DialogContent>
      ) : (
        <Form methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Stack spacing={3}>
              <Stack spacing={2}>
                <Typography variant='h4'>Provider</Typography>
                <Divider />
                <Stack
                  alignItems={{ xs: 'center' }}
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={6}>
                  <Stack
                    alignItems='center'
                    sx={{
                      flexShrink: 0,
                      width: { lg: '40%', md: '50%', xs: '100%' }
                    }}>
                    <ImageUploader
                      alt='Provider logo preview'
                      data-testid='uploadProviderLogo'
                      emptyPlaceholder='No logo uploaded'
                      imagePreviewSx={{ minHeight: 120 }}
                      innerImageSx={{ maxHeight: 100, maxWidth: 255 }}
                      invalidTypeMessage='Logo must be a PNG, JPEG, or WebP image.'
                      isUploading={isUploading}
                      label='Upload logo'
                      onChange={key =>
                        methods.setValue('providerLogoUri', key, {
                          shouldDirty: true,
                          shouldValidate: true
                        })
                      }
                      onUpload={({ content, contentType }) =>
                        uploadAsset.mutateAsync({
                          data: {
                            content,
                            contentType:
                              contentType as UploadAssetBodyContentType
                          },
                          kind: 'logos'
                        })
                      }
                      previewSrc={assetUrl(providerLogoUri)}
                      previewVariant='image'
                      registerCtx={methods.register('providerLogoUri')}
                      value={providerLogoUri}
                    />
                  </Stack>
                  <Stack
                    spacing={2}
                    sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <Input
                      data-testid='providerDescription'
                      format='string'
                      fullWidth
                      label='Provider Name'
                      labelInfo='Internal reference name used to identify the provider in the advisor control panel and in delete confirmations. Not shown to participants.'
                      name='description'
                      required
                      size='small'
                    />
                    <SelectInput
                      data-testid='providerMethodology'
                      disabled={isMethodologyLocked}
                      fullWidth
                      label='Investment Methodology'
                      name='managedAccountMethodologyId'
                      options={methodologies.data ?? []}
                      required
                      size='small'
                      startAdornmentTooltip={
                        isMethodologyLocked
                          ? 'Methodology cannot be changed while the provider is enabled for use. Disable the provider to make changes.'
                          : undefined
                      }
                    />
                    <Autocomplete
                      data-testid='providerWhiteLabelId'
                      fullWidth
                      getOptionValue={option => option.value}
                      label='White Label'
                      name='whiteLabelId'
                      options={whitelabelOptions}
                    />
                  </Stack>
                </Stack>
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>Dynamic QDIA</Typography>
                <Divider />
                <Stack
                  alignItems={{ md: 'flex-start', xs: 'stretch' }}
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={6}>
                  <SwitchInput
                    color='success'
                    hideError
                    label='Permitted'
                    name='permitsDynamicQDIA'
                    off='No'
                    on='Yes'
                    tooltip='When on, this provider can be used as a Dynamic QDIA: participants under the age threshold are auto-enrolled into a target-date fund instead of the managed account program. When off, all eligible participants go into the managed account regardless of age.'
                    tooltipIcon={<InfoOutlined />}
                  />
                  <Input
                    data-testid='providerDynamicQDIAAgeDefault'
                    format='number'
                    fullWidth
                    label='Default age threshold'
                    labelInfo='Default cutoff age applied across plans using this provider. Participants younger than this age are routed to the QDIA target-date fund; older participants enroll in the managed account. Plans can override this value.'
                    min={0}
                    name='dynamicQDIAAgeDefault'
                    readOnly={!permitsDynamicQDIA}
                    size='small'
                    startAdornmentIcon={
                      permitsDynamicQDIA ? undefined : <LockOutlined />
                    }
                    startAdornmentTooltip={
                      permitsDynamicQDIA
                        ? undefined
                        : 'Enable Dynamic QDIA to set a default age threshold.'
                    }
                  />
                </Stack>
                <Typography color='text.secondary' variant='caption'>
                  Can be overridden at the plan level.
                </Typography>
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>Display & Disclosures</Typography>
                <Divider />
                <Input
                  data-testid='providerFeeDisclosureMessage'
                  format='string'
                  fullWidth
                  label='Fee Disclosure Message'
                  labelInfo='Fee description shown to participants on the investment election screen and the methods/map-method modals when reviewing this managed account option.'
                  multiline
                  name='feeDisclosureMessage'
                  rows={2}
                  size='small'
                />
                {isIJoinAMA && (
                  <>
                    <Grid container spacing={2}>
                      <Grid size={{ md: 6, xs: 12 }}>
                        <Input
                          data-testid='providerDisplayName'
                          format='string'
                          fullWidth
                          label='Display Name'
                          labelInfo='Public-facing provider name shown to participants in disclosure modals during strategy authorization and on enrollment confirmation reports.'
                          name='providerDisplayName'
                          size='small'
                        />
                      </Grid>
                      <Grid size={{ md: 6, xs: 12 }}>
                        <Input
                          data-testid='providerDisplayPhoneNumber'
                          format='phone'
                          fullWidth
                          label='Phone'
                          labelInfo='Contact phone number shown to participants on the enrollment confirmation report alongside the display email.'
                          name='providerDisplayPhoneNumber'
                          size='small'
                        />
                      </Grid>
                      <Grid size={{ md: 6, xs: 12 }}>
                        <Input
                          data-testid='providerDisplayEmail'
                          format='email'
                          fullWidth
                          label='Email'
                          labelInfo='Contact email shown to participants on the enrollment confirmation report alongside the display phone number.'
                          name='providerDisplayEmail'
                          size='small'
                        />
                      </Grid>
                      <Grid size={{ md: 6, xs: 12 }}>
                        <Input
                          data-testid='providerAdvLink'
                          format='url'
                          fullWidth
                          label='ADV / Other Info Link'
                          labelInfo='URL rendered as a hyperlink in the participant disclosure modal during strategy authorization. Points to the provider ADV form or similar disclosure document.'
                          name='providerAdvLink'
                          size='small'
                        />
                      </Grid>
                    </Grid>
                    <Input
                      data-testid='providerAdditionalDisclosure'
                      format='string'
                      fullWidth
                      label='Additional Disclosures'
                      labelInfo='Additional disclosure text appended to the participant disclosure modal during strategy authorization.'
                      multiline
                      name='providerAdditionalDisclosure'
                      rows={2}
                      size='small'
                    />
                    <Input
                      data-testid='providerDisclosureStorage'
                      format='string'
                      fullWidth
                      label='CONNECT Disclosures'
                      labelInfo='Structured disclosure content stored for use by the CONNECT API. Populated by integrations to support downstream disclosure delivery.'
                      multiline
                      name='disclosureStorage'
                      rows={3}
                      size='small'
                    />
                    <SelectInput
                      data-testid='providerModelKey'
                      fullWidth
                      label='Model Key'
                      labelInfo='Backend integration identifier used to map this provider to its underlying submodel (e.g. Leafhouse). Set by the integration team, and used by the AMA API to resolve the model for every calculation.'
                      name='modelKey'
                      options={MODEL_KEY_OPTIONS}
                      required
                      size='small'
                    />
                  </>
                )}
              </Stack>

              {isCreating &&
                selectedMethodologyId ===
                  MANAGED_ACCOUNT_PROVIDERS.FIVE_FUND && (
                  <Alert data-testid='providerCreateFirstInfo' severity='info'>
                    Save this provider to configure funds, target rates of
                    return, and volatility descriptions.
                  </Alert>
                )}

              {isNoConfigMethodology && (
                <Alert data-testid='providerNoConfigInfo' severity='info'>
                  No configuration is needed. The selected MAP methodology will
                  dynamically assemble participant allocations using the plan's
                  available fund lineup.
                </Alert>
              )}

              <SwitchInput
                color='success'
                hideError
                label='Enabled for use'
                name='setupComplete'
                off='No'
                on='Yes'
                tooltip='When on, this provider becomes available for selection across the advisor org and can be assigned to plans. Leave off until display fields, disclosures, and integration setup (model key) are complete.'
                tooltipIcon={<InfoOutlined />}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='cancelProviderForm'
              onClick={onCancel}
              variant='outlined'>
              Cancel
            </Button>
            {/* legacy AMA providers carry no model key, so validate an existing
                one on open: without this the required field blocks save with a
                dead button and no message anywhere on the form. A new provider
                starts empty, so validating it on open is just noise */}
            <FormButton
              data-testid='saveProviderForm'
              disabled={isSaveDisabled}
              triggerValidation={!isCreating}>
              {isCreating ? 'Create' : 'Save'}
            </FormButton>
          </DialogActions>
        </Form>
      )}
    </Dialog>
  );
};

ManagedAccountProviderFormDialog.displayName =
  'ManagedAccountProviderFormDialog';
